import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React, { useMemo } from "react"

import { FilterChip } from "@/components/common/Filters"
import { CompanyStatus } from "@/graphql/codegen/graphql"
import { useGetBaseCompanies } from "@/hooks/useGetBaseCompanies"
import { CompanyType } from "@/types/companyType"

const CompetitorProductsFilterChips: FC = () => {
  const { distributorIds } = useSearch({
    from: "/app/admin/manage-products/_layout/competitor-products",
    strict: true,
  })
  const navigate = useNavigate({ from: "/app/admin/manage-products/competitor-products" })

  const { companies } = useGetBaseCompanies({
    types: [CompanyType.Customer, CompanyType.Vendor],
    statuses: [CompanyStatus.Active],
  })

  const distributorNames = useMemo(() => {
    if (!distributorIds || distributorIds.length === 0) return []
    return distributorIds
      .map((id) => {
        const company = companies.find((c) => c.companyId === id)
        return company?.name
      })
      .filter((name): name is string => name !== undefined)
  }, [distributorIds, companies])

  const hasFilters = distributorIds && distributorIds.length > 0

  if (!hasFilters) {
    return null
  }

  const handleClearDistributor = (distributorId: string) => {
    const updatedDistributorIds = distributorIds?.filter((id) => id !== distributorId)
    navigate({
      search: (prev) => ({
        ...prev,
        distributorIds: updatedDistributorIds,
      }),
      replace: true,
    })
  }

  return (
    <Box data-testid='competitor-products-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>
      {distributorNames.map((name) => {
        const id = distributorIds?.[distributorNames.indexOf(name)]
        return id ? (
          <FilterChip key={`distributor-${id}`} label={name} onDelete={() => handleClearDistributor(id)} />
        ) : null
      })}
    </Box>
  )
}

export default CompetitorProductsFilterChips
